import { useEffect, useMemo, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  addressMasterApi,
  userAwardsCertificateApi,
  userLoginApi,
  userMasterApi,
  userSocialMediaProfileApi,
} from "../../api/masterApi";
import { getAdminUser, ROLE_CODES } from "../../utils/auth";
import VerifyUserModal from "./VerifyUserModal";
import "../../components/MasterCrud.css";
import "./VerifierUserLogins.css";

const USER_VERIFY_FLAGS = [
  "education_verified",
  "criminal_record_verified",
  "business_integrity_verified",
  "kyc_verified",
  "bank_details_verified",
];

/** Strip any password fields so they never appear in verifier UI state */
function sanitizeLogin(row) {
  if (!row || typeof row !== "object") return row;
  const { password_plain, password_hash, password, ...safe } = row;
  return safe;
}

function isVerifierLogin(row) {
  const code = String(row?.user_role_code || "").toLowerCase();
  const role = String(row?.user_role || "").toLowerCase();
  return (
    code === ROLE_CODES.VERIFIER ||
    role === "verifier" ||
    role === "verifyer"
  );
}

function groupByUserId(rows = [], key = "user_id") {
  const map = new Map();
  (rows || []).forEach((row) => {
    const id = String(row?.[key] ?? "");
    if (!id) return;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push(row);
  });
  return map;
}

function isUserDetailsVerified(user, addresses = [], social = [], awards = []) {
  if (!user) return false;
  const userOk = USER_VERIFY_FLAGS.every((flag) => Number(user[flag]) === 1);
  if (!userOk) return false;
  const addressesOk =
    addresses.length === 0 ||
    addresses.every((row) => Number(row.address_verified) === 1);
  const socialOk =
    social.length === 0 ||
    social.every((row) => Number(row.social_media_verified) === 1);
  const awardsOk =
    awards.length === 0 ||
    awards.every((row) => Number(row.awards_verified) === 1);
  return addressesOk && socialOk && awardsOk;
}

export default function VerifierUserLogins() {
  const [rows, setRows] = useState([]);
  const [verifiedUserIds, setVerifiedUserIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });

  const showToast = (message, severity = "error") => {
    setToast({ open: true, message, severity });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const current = getAdminUser();
      const currentLoginId = current?.login_id;
      const [logins, users, addresses, social, awards] = await Promise.all([
        userLoginApi.list(),
        userMasterApi.list(),
        addressMasterApi.list(),
        userSocialMediaProfileApi.list(),
        userAwardsCertificateApi.list(),
      ]);

      const filtered = (logins || [])
        .map(sanitizeLogin)
        .filter((row) => {
          if (isVerifierLogin(row)) return false;
          if (
            currentLoginId != null &&
            String(row.login_id) === String(currentLoginId)
          ) {
            return false;
          }
          return true;
        });

      const userById = new Map(
        (users || []).map((user) => [String(user.user_id), user])
      );
      const addressesByUser = groupByUserId(addresses);
      const socialByUser = groupByUserId(social);
      const awardsByUser = groupByUserId(awards);

      const verified = new Set();
      filtered.forEach((row) => {
        const userId = String(row.reference_id ?? "");
        if (!userId) return;
        if (
          isUserDetailsVerified(
            userById.get(userId),
            addressesByUser.get(userId) || [],
            socialByUser.get(userId) || [],
            awardsByUser.get(userId) || []
          )
        ) {
          verified.add(userId);
        }
      });

      setRows(filtered);
      setVerifiedUserIds(verified);
    } catch (error) {
      showToast(error.message || "Failed to load user logins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaved = async () => {
    showToast("Verification saved successfully", "success");
    await loadData();
  };

  const verifiedCount = useMemo(() => verifiedUserIds.size, [verifiedUserIds]);

  return (
    <div className="master-page">
      <div className="master-card">
        <div className="master-card__header">
          <h2>User Details</h2>
          <p>
            All user login accounts
            {!loading && verifiedCount > 0
              ? ` · ${verifiedCount} verified`
              : ""}
          </p>
        </div>

        <div className="master-table-wrap">
          <table className="master-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Login ID</th>
                <th>User Role</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="master-table__empty">
                    No user logins found.
                  </td>
                </tr>
              )}
              {rows.map((item, index) => {
                const userId = String(item.reference_id ?? "");
                const isVerified = userId && verifiedUserIds.has(userId);
                return (
                  <tr key={item.login_id}>
                    <td>{index + 1}</td>
                    <td>{item.login_id}</td>
                    <td>{item.user_role || "—"}</td>
                    <td>{item.name || "—"}</td>
                    <td>{item.phone || "—"}</td>
                    <td>{item.email || "—"}</td>
                    <td>{Number(item.status) === 1 ? "Active" : "Inactive"}</td>
                    <td>
                      <div className="master-table__actions">
                        {isVerified ? (
                          <button
                            type="button"
                            className="verifier-verified-btn"
                            title="Verified user — click to review"
                            aria-label="Verified user"
                            onClick={() => setVerifyTarget(item)}
                          >
                            <span>Verified User</span>
                            <CheckCircleIcon />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="master-btn master-btn--edit"
                            onClick={() => setVerifyTarget(item)}
                          >
                            Verify User
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <VerifyUserModal
        open={Boolean(verifyTarget)}
        loginRow={verifyTarget}
        onClose={() => setVerifyTarget(null)}
        onSaved={handleSaved}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
